
import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import {
  Users,
  Target,
  Eye,
  Heart,
  Award,
  Shield,
  Zap,
  TrendingUp,
  Globe,
  Clock,
  Star,
  CheckCircle,
  ArrowRight
} from "lucide-react";

const Nosotros = () => {

  // 👇 Scroll siempre al inicio cuando se carga el componente
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);



  const stats = [
    { icon: Users, value: "500+", label: "Clientes Satisfechos" },
    { icon: Award, value: "3+", label: "Años de Experiencia" },
    { icon: CheckCircle, value: "99%", label: "Satisfacción del Cliente" },
    { icon: Globe, value: "50+", label: "Proyectos Completados" }
  ];

  const values = [
    {
      icon: Shield,
      title: "Transparencia",
      description: "Creemos en la honestidad y claridad en cada proceso y comunicación con nuestros clientes."
    },
    {
      icon: Heart,
      title: "Compromiso",
      description: "Nos dedicamos al 100% a superar las expectativas y entregar resultados excepcionales."
    },
    {
      icon: Zap,
      title: "Innovación",
      description: "Siempre buscamos las últimas tendencias y tecnologías para ofrecer soluciones vanguardistas."
    },
    {
      icon: TrendingUp,
      title: "Excelencia",
      description: "No nos conformamos con lo bueno, perseguimos la perfección en cada detalle."
    }
  ];

  const team = [
    {
      name: "Ana Rodríguez",
      role: "CEO & Fundadora",
      image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
      description: "Más de 10 años de experiencia en desarrollo de negocios digitales."
    },
    {
      name: "Carlos Mendoza",
      role: "Director de Tecnología",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
      description: "Especialista en arquitecturas escalables y tecnologías emergentes."
    },
    {
      name: "Laura Silva",
      role: "Directora de Diseño",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80",
      description: "Apasionada por crear experiencias de usuario memorables y funcionales."
    }
  ];

  const process = [
    {
      step: "01",
      title: "Consulta y Análisis",
      description: "Comprendemos tus necesidades y objetivos para crear una estrategia personalizada."
    },
    {
      step: "02",
      title: "Planificación Estratégica",
      description: "Desarrollamos un plan detallado con cronogramas y entregables claros."
    },
    {
      step: "03",
      title: "Ejecución y Desarrollo",
      description: "Implementamos las soluciones con metodologías ágiles y mejores prácticas."
    },
    {
      step: "04",
      title: "Entrega y Soporte",
      description: "Garantizamos resultados y ofrecemos soporte continuo post-entrega."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/20">
      {/* HERO SECTION */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="px-6 py-3 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold inline-flex items-center gap-2 mb-6"
            >
              <Star className="w-4 h-4 fill-current" />
              Líderes en Soluciones Digitales
            </motion.span>

            <h1 className="text-5xl lg:text-7xl font-extrabold text-gray-900 mb-6">
              Conoce Nuestra{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Historia
              </span>
            </h1>

            <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Somos un equipo apasionado de profesionales dedicados a transformar ideas
              en <span className="text-blue-600 font-semibold">soluciones digitales excepcionales</span> que impulsan
              el crecimiento de tu negocio.
            </p>
          </motion.div>

          {/* STATS GRID */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
              >
                <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-gray-600 text-sm font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* MISSION & VISION SECTION - FONDOS SUAVIZADOS */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* MISIÓN - Fondo suavizado */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100 relative overflow-hidden group hover:shadow-xl transition-all duration-500"
            >
              {/* Elementos decorativos sutiles */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200/20 rounded-full -translate-y-16 translate-x-16 group-hover:scale-110 transition-transform duration-700"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-indigo-200/20 rounded-full translate-y-12 -translate-x-12 group-hover:scale-110 transition-transform duration-700"></div>

              <div className="relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestra Misión</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Impulsar el éxito digital de nuestros clientes mediante soluciones innovadoras
                  y personalizadas. Nos comprometemos a entregar excelencia en cada proyecto,
                  garantizando calidad, transparencia y resultados medibles que superen expectativas.
                </p>
              </div>
            </motion.div>

            {/* VISIÓN - Fondo suavizado */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border border-purple-100 relative overflow-hidden group hover:shadow-xl transition-all duration-500"
            >
              {/* Elementos decorativos sutiles */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-purple-200/20 rounded-full -translate-y-16 -translate-x-16 group-hover:scale-110 transition-transform duration-700"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-pink-200/20 rounded-full translate-y-12 translate-x-12 group-hover:scale-110 transition-transform duration-700"></div>

              <div className="relative z-10">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Eye className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Nuestra Visión</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Ser reconocidos como el partner estratégico preferido en transformación digital,
                  destacándonos por nuestra innovación constante, compromiso con la excelencia y
                  capacidad para anticipar las necesidades del mercado global.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Nuestros <span className="text-blue-600">Valores</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Principios que guían cada decisión y acción en nuestra organización
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-20 px-6 bg-gray-50/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Nuestro <span className="text-blue-600">Proceso</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Metodología probada que garantiza el éxito de cada proyecto
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group h-full">
                  <div className="text-4xl font-black text-blue-600/20 mb-4">{step.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>

                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-blue-200 transform -translate-y-1/2 z-10"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Conoce al <span className="text-blue-600">Equipo</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Profesionales apasionados que hacen posible la magia
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 group text-center"
              >
                <div className="w-24 h-24 mx-auto mb-4 rounded-2xl overflow-hidden border-4 border-white shadow-lg group-hover:scale-105 transition-transform duration-300">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                <div className="text-blue-600 font-semibold mb-3">{member.role}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              ¿Listo para comenzar tu proyecto?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Únete a nuestros clientes satisfechos y lleva tu negocio al siguiente nivel
            </p>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.2)" }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-white text-blue-600 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-3 mx-auto"
            >
              <span>Contactar Ahora</span>
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Nosotros;